import { z } from 'zod';

import { ComponentValidator, ComponentPayloadValidator } from '../Component';

import { IsolationVoltage } from '../fields/IsolationVoltage';

import { AbstractPort, Port } from '../fields/AbstractPort';
import { ParallelableCapacity } from '../fields/ParallelableCapacity';

import { ComponentDefinition } from '../../types/ComponentDefinition';

const port = AbstractPort.extend({
    AC: Port.AC.extend({
        controlMethods: Port.controlMethods.validator,
    }).default({}),
    isolated: Port.isolated.validator.default(true),
    parallelableCapacity: ParallelableCapacity.validator,
    powerFlowDirection: Port.powerFlowDirection.validator.default('bidirectional'),
}).default({
    powerFlowDirection: 'bidirectional',
});

const electrical = z
    .object({
        ports: z.array(port).min(2).max(3).default([undefined, undefined]),
        standards: z.array(z.string()).default([]),
        isolationVoltage: IsolationVoltage.validator,
    })
    .default({});

const TransformerBase = z.object({
    type: z.literal('transformer').default('transformer'),
    electrical,
});

export const TransformerValidator = ComponentValidator.merge(TransformerBase);
export const TransformerPayloadValidator = ComponentPayloadValidator.merge(TransformerBase);

export const TransformerDefinition: ComponentDefinition = {
    type: 'transformer',
    name: 'Transformer',
    plural: 'Transformers',
    indicator: 'T',
    hasDynamicNumberOfPorts: true,
    interoperabilityDataModel: false,
    actsAsConverter: true,
    ports: {
        min: 2,
        max: 3,
        powerFlowDirections: ['input', 'output', 'bidirectional'],
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: TransformerValidator,
        payload: TransformerPayloadValidator,
    },
};

export type Transformer = z.infer<typeof TransformerValidator>;
export type TransformerPayload = z.infer<typeof TransformerPayloadValidator>;
